import { useEffect, useState, type SyntheticEvent } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import { readProblem } from '../lib/problem';
import { useCreateGroup } from '../model/useFormatLibrary';

const KEY = /^[a-z0-9]+(-[a-z0-9]+)*$/;

interface NewGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewGroupDialog({ open, onOpenChange }: NewGroupDialogProps) {
  const { t } = useTranslation('admin');
  const create = useCreateGroup();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKey('');
    setName('');
    setSubmitted(false);
  }, [open]);

  const keyError = !KEY.test(key) ? t('admin.adFormats.newFormatDialog.keyInvalid') : undefined;
  const nameError = !name.trim() ? t('admin.adFormats.newFormatDialog.required') : undefined;

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (keyError || nameError) return;
    try {
      await create.mutateAsync({ key, name: name.trim() });
      onOpenChange(false);
    } catch (error) {
      notificationService.showError(
        readProblem(error).code === 'already_exists'
          ? t('admin.adFormats.errors.alreadyExists')
          : t('admin.adFormats.errors.generic'),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-md">
          <DialogHeader>
            <DialogTitle>{t('admin.adFormats.newGroup')}</DialogTitle>
            <DialogDescription>{t('admin.adFormats.newGroupDialog.description')}</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="new-group-name">{t('admin.adFormats.fields.name')}</FieldLabel>
            <Input
              id="new-group-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            {submitted && nameError && <FieldError>{nameError}</FieldError>}
          </Field>
          <Field>
            <FieldLabel htmlFor="new-group-key">{t('admin.adFormats.fields.key')}</FieldLabel>
            <Input
              id="new-group-key"
              value={key}
              onChange={(event) => setKey(event.target.value)}
            />
            <FieldDescription>{t('admin.adFormats.newGroupDialog.keyHint')}</FieldDescription>
            {submitted && keyError && <FieldError>{keyError}</FieldError>}
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('admin.adFormats.cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t('admin.adFormats.saving')
                : t('admin.adFormats.newGroupDialog.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
